import { Multiselect, Select } from '@cloudscape-design/components';
import React, { useEffect, useState } from 'react';

import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemsGroups, OptionDefinition } from '../../resources/types';

// Assuming OptionDefinition = { label: string; value: string; }

// --- Props Definitions ---

// The return type is changed from ItemsGroups | undefined to number | undefined
interface SelectGroupSingleProps {
  single: true;
  visible?: boolean;
  value?: number; // Single value: number (idGroup)
  readonly?: boolean;
  width?: number;
  tokenLimit?: number;
  hideTokens?: boolean;
  // CHANGED: returns number (idGroup) or undefined
  onSelect?: (selectedId: number | undefined) => void;
}

// The return type is changed from ItemsGroups[] to number[]
interface SelectGroupMultiProps {
  single?: false; // default is "false"
  visible?: boolean;
  value?: number[]; // Multi value: number[] (idGroup list)
  readonly?: boolean;
  width?: number;
  tokenLimit?: number;
  hideTokens?: boolean;
  // CHANGED: returns number[] (idGroup list)
  onSelect?: (selectedIds: number[]) => void;
}

export type SelectGroupProps =
  | SelectGroupSingleProps
  | SelectGroupMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectGroup: React.FC<SelectGroupProps> = (props) => {
  const { single, value, readonly, onSelect, width = 200, tokenLimit = 3, hideTokens = false } = props;
  const { data } = useGlobalState();

  // Utility array that maps raw data to the full ItemGroup structure
  const mappedGroups: ItemsGroups[] = data.groups.map((plan: ItemsGroups) => ({
    idGroup: plan.idGroup,
    group: plan.group,
    value: plan.idGroup.toString(),
    label: plan.group,
  }));

  // Create a separate array that is strictly OptionDefinition[] for the 'options' prop
  const groupOptions: OptionDefinition[] = mappedGroups.map(group => ({
    label: group.label,
    value: group.value,
  }));

  // --- State Initialization (No change needed here) ---

  // State for Single Select: stores a simplified OptionDefinition object
  const [selectedSingle, setSelectedSingle] = useState<OptionDefinition | undefined>(
    single
      ? (() => {
        const match = mappedGroups.find(c => c.idGroup === value);
        return match ? { label: match.label, value: match.value } : undefined;
      })()
      : undefined
  );

  // State for Multi Select: stores OptionDefinition[]
  const [selectedMulti, setSelectedMulti] = useState<OptionDefinition[]>(
    !single && Array.isArray(value)
      ? value
        .map(v => {
          const match = mappedGroups.find((c: ItemsGroups) => c.idGroup === v);
          if (!match) return null;
          return {
            label: match.label,
            value: match.value,
          } as OptionDefinition;
        })
        .filter((x): x is OptionDefinition => x !== null)
      : []
  );

  // --- Effects (No change needed here) ---

  // Update internal state when the external 'value' prop changes
  useEffect(() => {
    if (single) {
      const match = mappedGroups.find(c => c.idGroup === value);
      if (match) {
        setSelectedSingle({ label: match.label, value: match.value });
      } else {
        setSelectedSingle(undefined);
      }
    } else if (Array.isArray(value)) {
      const multiOptions = value
        .map(v => {
          const match = mappedGroups.find((c: ItemsGroups) => c.idGroup === v);
          if (!match) return null;
          return { label: match.label, value: match.value } as OptionDefinition;
        })
        .filter((x): x is OptionDefinition => x !== null);
      setSelectedMulti(multiOptions);
    }
  }, [value, single, data.groups]);

  const placeholder = single ? "Seleccionar grupo" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar grupos";
  return (
    <div style={width ? { width: `${width}px` } : {}}>
      {/* Single Select Mode */}
      {single && (
        <Select
          readOnly={readonly}
          placeholder={placeholder}
          selectedOption={selectedSingle ?? null}
          options={groupOptions}
          filteringType="auto"
          onChange={({ detail }) => {
            const idString = detail.selectedOption?.value;
            const fullGroup = idString ? Number(idString) : undefined; // The selected ID (number)

            // Update internal state (OptionDefinition)
            if (fullGroup) {
              const match = mappedGroups.find(g => g.idGroup === fullGroup);
              setSelectedSingle(match ? { label: match.label, value: match.value } : undefined);
            } else {
              setSelectedSingle(undefined);
            }

            // Call external handler (number | undefined)
            const singleProps = props as SelectGroupSingleProps;
            if (singleProps.onSelect) {
              // CHANGED: Pass the ID (number) or undefined
              singleProps.onSelect(fullGroup);
            }
          }}
        />
      )}

      {/* Multi Select Mode */}
      {!single && (
        <Multiselect
          placeholder={placeholder}
          hideTokens={hideTokens}
          tokenLimit={tokenLimit}
          readOnly={readonly}
          selectedOptions={selectedMulti}
          filteringType="auto"
          options={groupOptions}
          onChange={({ detail }) => {
            const list = detail.selectedOptions as OptionDefinition[];

            // Update internal state
            setSelectedMulti([...list]);

            const multiProps = props as SelectGroupMultiProps;

            if (multiProps.onSelect) {
              // CHANGED: Map OptionDefinition[] to number[]
              const selectedIds: number[] = list
                .map(o => Number(o.value)) // Convert value string back to number ID
                .filter(id => !isNaN(id)); // Filter out any invalid numbers

              multiProps.onSelect(selectedIds);
            }
          }}
        />
      )}
    </div>
  );
};

export default SelectGroup;